import * as React from 'react';
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow,
    TextField, Typography
} from '@mui/material';
import {MaterialTraceability} from '../../../controllers';
import {CONST, TokenValidator} from '../../../utils';

const displayTime = value => value ? new Date(value).toLocaleString() : '';

export default function MaterialTraceabilityPanel({onStateChange, refreshKey = 0}) {
    const [machines, setMachines] = React.useState([]);
    const [selectedId, setSelectedId] = React.useState('');
    const [recent, setRecent] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [changeOpen, setChangeOpen] = React.useState(false);
    const [newRoll, setNewRoll] = React.useState('');
    const [saving, setSaving] = React.useState(false);

    const selected = React.useMemo(
        () => machines.find(machine => String(machine.id) === String(selectedId)) || null,
        [machines, selectedId]
    );

    const token = React.useCallback(() => TokenValidator.GetStaffUser(), []);

    const loadMachines = React.useCallback(async () => {
        setLoading(true);
        setError('');
        const staffToken = await token();
        const response = await MaterialTraceability.Machines(staffToken);
        setLoading(false);
        if (!response || !response.success) {
            setError(response ? response.message : 'Unable to load ATTLAS machines.');
            setMachines([]);
            return;
        }
        setMachines(response.result);
        const preferred = response.result.find(machine => machine.preferred);
        const saved = localStorage.getItem(CONST.STORAGE_KEYS.MATERIAL_TRACE_MACHINE);
        const workstationMachine = response.result.find(machine => String(machine.id) === saved);
        const initial = preferred || workstationMachine;
        setSelectedId(initial ? String(initial.id) : '');
        if (initial) localStorage.setItem(CONST.STORAGE_KEYS.MATERIAL_TRACE_MACHINE, String(initial.id));
        if (!preferred && workstationMachine) {
            await MaterialTraceability.SelectMachine(staffToken, workstationMachine.id);
        }
    }, [token]);

    React.useEffect(() => { loadMachines(); }, [loadMachines]);

    React.useEffect(() => {
        onStateChange({
            machine: selected,
            activeRoll: selected ? selected.activeRoll : null,
            ready: Boolean(selected && selected.activeRoll),
            canChangeRoll: true
        });
    }, [selected, onStateChange]);

    const refreshMachine = React.useCallback(async () => {
        if (!selectedId) {
            setRecent([]);
            return;
        }
        const staffToken = await token();
        const [rollResponse, recentResponse] = await Promise.all([
            MaterialTraceability.ActiveRoll(staffToken, selectedId),
            MaterialTraceability.Recent(staffToken, selectedId, 5)
        ]);
        if (!rollResponse || !rollResponse.success) {
            setError(rollResponse ? rollResponse.message : 'Unable to load Active Roll.');
            return;
        }
        setMachines(current => current.map(machine => String(machine.id) === String(selectedId)
            ? {...machine, activeRoll: rollResponse.result.activeRoll}
            : machine));
        if (recentResponse && recentResponse.success) setRecent(recentResponse.result);
    }, [selectedId, token]);

    React.useEffect(() => { refreshMachine(); }, [refreshMachine, refreshKey]);

    const chooseMachine = async event => {
        const value = event.target.value;
        setError('');
        if (!value) return;
        setSaving(true);
        const response = await MaterialTraceability.SelectMachine(await token(), value);
        setSaving(false);
        if (!response || !response.success) {
            setError(response ? response.message : 'Unable to save machine selection.');
            return;
        }
        setSelectedId(String(value));
        localStorage.setItem(CONST.STORAGE_KEYS.MATERIAL_TRACE_MACHINE, String(value));
        setMachines(current => current.map(machine => ({...machine, preferred: String(machine.id) === String(value)})));
    };

    const saveRoll = async () => {
        if (!selected || !newRoll.trim()) return;
        setSaving(true);
        setError('');
        const response = await MaterialTraceability.ChangeRoll(await token(), selected.id, newRoll);
        setSaving(false);
        if (!response || !response.success) {
            setError(response ? response.message : 'Unable to change Active Roll.');
            return;
        }
        setChangeOpen(false);
        setNewRoll('');
        await refreshMachine();
    };

    return (
        <Paper variant='outlined' sx={{p: 2, mb: 2}}>
            <Stack gap={2}>
                <Typography variant='h6'>MATERIAL TRACEABILITY</Typography>
                {error && <Alert severity='error' onClose={() => setError('')}>{error}</Alert>}
                <Stack direction={{xs: 'column', md: 'row'}} gap={2} alignItems={{md: 'center'}}>
                    <TextField select size='small' label='Machine' value={selectedId} onChange={chooseMachine}
                        disabled={loading || saving} sx={{minWidth: 220}}>
                        <MenuItem value=''>Select machine</MenuItem>
                        {machines.map(machine => <MenuItem key={machine.id} value={String(machine.id)}>{machine.name}</MenuItem>)}
                    </TextField>
                    <Typography variant='caption' color='text.secondary'>Selection is saved to your MES user account.</Typography>
                    <Box sx={{minWidth: 220}}>
                        <Typography variant='caption' color='text.secondary'>Active Roll</Typography>
                        <Typography>{selected && selected.activeRoll ? selected.activeRoll.rollNumber : 'Not assigned'}</Typography>
                    </Box>
                    <Button variant='outlined' onClick={() => setChangeOpen(true)} disabled={!selected}>Change Roll</Button>
                    <Chip color={selected && selected.activeRoll ? 'success' : 'warning'}
                        label={selected && selected.activeRoll ? 'READY' : 'NOT READY'} />
                </Stack>

                {selected && <Box>
                    <Typography variant='subtitle2' sx={{mb: 1}}>RECENT TRACEABILITY — {selected.name}</Typography>
                    <Table size='small'>
                        <TableHead><TableRow>
                            <TableCell>Date/Time</TableCell><TableCell>Roll</TableCell><TableCell>Order</TableCell>
                            <TableCell>Case ID</TableCell><TableCell>Client</TableCell><TableCell align='right'>Pieces</TableCell>
                        </TableRow></TableHead>
                        <TableBody>
                            {recent.map(row => <TableRow key={row.id}>
                                <TableCell>{displayTime(row.registeredAt)}</TableCell><TableCell>{row.rollNumber}</TableCell>
                                <TableCell>{row.orderId}</TableCell><TableCell>{row.caseId}</TableCell>
                                <TableCell>{row.client}</TableCell><TableCell align='right'>{row.pieces}</TableCell>
                            </TableRow>)}
                            {!recent.length && <TableRow><TableCell colSpan={6} align='center'>No traceability records for this machine.</TableCell></TableRow>}
                        </TableBody>
                    </Table>
                </Box>}
            </Stack>

            <Dialog open={changeOpen} onClose={() => !saving && setChangeOpen(false)} maxWidth='sm' fullWidth>
                <DialogTitle>Change Roll</DialogTitle>
                <DialogContent><Stack gap={2} sx={{pt: 1}}>
                    <TextField label='Machine' value={selected ? selected.name : ''} InputProps={{readOnly: true}} />
                    <TextField label='Current Roll' value={selected && selected.activeRoll ? selected.activeRoll.rollNumber : 'Not assigned'}
                        InputProps={{readOnly: true}} />
                    <TextField autoFocus required label='New Roll' value={newRoll} inputProps={{maxLength: 128}}
                        onChange={event => setNewRoll(event.target.value)} />
                </Stack></DialogContent>
                <DialogActions>
                    <Button onClick={() => setChangeOpen(false)} disabled={saving}>Cancel</Button>
                    <Button variant='contained' onClick={saveRoll} disabled={saving || !newRoll.trim()}>Save New Roll</Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}
